<template>
  <div class="device-admin-page">
    <section class="device-hero">
      <div>
        <p class="eyebrow">QRWiFi Device</p>
        <h1>设备管理</h1>
        <p class="description">统一管理二维码与NFC设备。新增设备时只填写备注，设备ID、小程序二维码和 NFC_URL 由系统自动生成。</p>
      </div>
      <div class="device-hero__actions">
        <el-button type="primary" size="large" @click="openAdd">添加设备</el-button>
        <el-button size="large" @click="openBatch">批量添加</el-button>
        <el-button size="large" @click="exportDeviceList">导出设备</el-button>
      </div>
    </section>

    <section class="device-table-card">
      <div class="toolbar">
        <div>
          <h2>设备列表</h2>
          <p>展示系统生成的设备ID、备注、小程序二维码、NFC_URL、绑定信息和创建时间。</p>
        </div>
        <div class="toolbar__actions">
          <el-input
            v-model="query.keyword"
            clearable
            placeholder="搜索设备ID、备注或绑定信息"
            class="search-input"
            @keyup.enter="loadData"
            @clear="loadData"
          />
          <el-button @click="loadData">搜索</el-button>
        </div>
      </div>

      <el-table v-loading="loading" :data="tableData" border stripe>
        <el-table-column prop="id" label="设备ID" width="180" />
        <el-table-column prop="remark" label="备注" min-width="180">
          <template #default="{ row }">
            <span>{{ row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="二维码" width="150" align="center">
          <template #default="{ row }">
            <div v-if="row.qrCodeUrl" class="device-qrcode" @click="previewQrCode(row)">
              <img class="device-qrcode__img" :src="resolveAssetUrl(row.qrCodeUrl)" alt="设备二维码" />
              <span class="device-qrcode__tip">点击放大</span>
            </div>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="NFC_URL" min-width="320">
          <template #default="{ row }">
            <el-link v-if="row.nfcUrl" :href="row.nfcUrl" type="primary" target="_blank" class="scheme-link">{{ row.nfcUrl }}</el-link>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="绑定信息" min-width="220">
          <template #default="{ row }">
            <div v-if="hasBinding(row)" class="bind-info">
              <div class="bind-info__title">{{ row.storeName || row.bind?.storeName || row.ssid || row.bind?.ssid || '已绑定' }}</div>
              <div v-if="row.wifiCodeId || row.bind?.wifiCodeId" class="bind-info__sub">店铺ID：{{ row.wifiCodeId || row.bind?.wifiCodeId }}</div>
              <div v-if="row.ownerOpenid || row.bind?.ownerOpenid" class="bind-info__sub">
                {{ row.ownerOpenid || row.bind?.ownerOpenid }}
              </div>
            </div>
            <span v-else class="empty-text">未绑定</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180" />
        <el-table-column label="操作" width="100" fixed="right" align="center">
          <template #default="{ row }">
            <el-button link type="danger" @click="removeDevice(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <el-pagination
          v-model:current-page="query.page"
          v-model:page-size="query.pageSize"
          background
          layout="prev, pager, next, sizes, total"
          :total="total"
          @current-change="loadData"
          @size-change="loadData"
        />
      </div>
    </section>

    <el-dialog v-model="addDialogVisible" title="添加设备" width="520px">
      <el-alert
        title="设备ID、小程序二维码和 NFC_URL 将由后端自动生成。"
        type="info"
        show-icon
        :closable="false"
        class="form-tip"
      />
      <el-form :model="form" label-width="80px">
        <el-form-item label="备注">
          <el-input v-model="form.remark" placeholder="请输入备注（可选）" maxlength="120" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveDevice">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="batchDialogVisible" title="批量添加" width="520px">
      <el-alert
        title="只需填写数量和一个备注，系统会批量生成设备ID、小程序二维码和 NFC_URL。导出时后端会生成包含 xlsx 和二维码图片的 zip 包。"
        type="info"
        show-icon
        :closable="false"
        class="form-tip"
      />
      <el-form label-width="96px">
        <el-form-item label="生成数量" required>
          <el-input-number v-model="batchForm.count" :min="1" :max="500" :step="1" controls-position="right" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="batchForm.remark" placeholder="请输入统一备注（可选）" maxlength="120" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveBatchDevices">批量生成</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="qrPreviewVisible" title="设备二维码" width="420px" align-center>
      <div v-if="previewDevice?.qrCodeUrl" class="qrcode-preview-dialog">
        <img class="qrcode-preview-dialog__img" :src="resolveAssetUrl(previewDevice.qrCodeUrl)" alt="设备二维码" />
        <div class="qrcode-preview-dialog__id">设备ID：{{ previewDevice.id }}</div>
        <el-link
          :href="resolveAssetUrl(previewDevice.qrCodeUrl)"
          type="primary"
          target="_blank"
          class="scheme-link"
        >
          {{ previewDevice.qrCodeUrl }}
        </el-link>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useApi } from '@/hooks/useApi';
import type { QrWifiDevice } from '@/api';

const loading = ref(false);
const addDialogVisible = ref(false);
const batchDialogVisible = ref(false);
const qrPreviewVisible = ref(false);
const previewDevice = ref<QrWifiDevice>();
const tableData = ref<QrWifiDevice[]>([]);
const total = ref(0);
const query = reactive({
  keyword: '',
  page: 1,
  pageSize: 10,
});
const form = reactive<Partial<QrWifiDevice>>({
  remark: '',
});
const batchForm = reactive({
  count: 1,
  remark: '',
});

const resetForm = () => {
  form.remark = '';
};

const hasBinding = (row: QrWifiDevice) => {
  const fields = [row.bind, row.wifiCodeId, row.storeName, row.ssid, row.ownerOpenid];
  return fields.some(Boolean);
};

const previewQrCode = (row: QrWifiDevice) => {
  previewDevice.value = row;
  qrPreviewVisible.value = true;
};

const resolveAssetUrl = (url?: string) => {
  const value = String(url || '').trim();
  if (!value) {
    return '';
  }
  if (/^https?:\/\//iu.test(value)) {
    return value;
  }
  const basePath = useApi().qrWifiApi.basePath || '';
  return `${basePath}${value.startsWith('/') ? value : `/${value}`}`;
};


const formatBinding = (row: QrWifiDevice) => {
  if (!hasBinding(row)) {
    return '未绑定';
  }
  return [
    row.storeName || row.bind?.storeName || row.ssid || row.bind?.ssid,
    row.wifiCodeId || row.bind?.wifiCodeId ? `店铺ID：${row.wifiCodeId || row.bind?.wifiCodeId}` : '',
    row.ownerOpenid || row.bind?.ownerOpenid || '',
  ].filter(Boolean).join(' / ');
};

const loadData = async() => {
  loading.value = true;
  try {
    const res = await useApi().qrWifiApi.pageDevices(query.keyword, query.page, query.pageSize);
    if (res.success && res.data) {
      tableData.value = res.data.data || [];
      total.value = res.data.total || 0;
    }
  } finally {
    loading.value = false;
  }
};

const openAdd = () => {
  resetForm();
  addDialogVisible.value = true;
};

const saveDevice = async() => {
  const res = await useApi().qrWifiApi.addDevice({ remark: form.remark });
  if (res.success) {
    ElMessage.success('添加成功，设备ID、二维码和 NFC_URL 已自动生成');
    addDialogVisible.value = false;
    await loadData();
  }
};

const openBatch = () => {
  batchForm.count = 1;
  batchForm.remark = '';
  batchDialogVisible.value = true;
};

const saveBatchDevices = async() => {
  if (!batchForm.count || batchForm.count < 1) {
    ElMessage.warning('请输入生成数量');
    return;
  }
  const res = await useApi().qrWifiApi.batchAddDevices({ count: batchForm.count, remark: batchForm.remark });
  if (res.success) {
    ElMessage.success('批量生成成功');
    batchDialogVisible.value = false;
    await loadData();
  }
};

const downloadFile = (url: string) => {
  const link = document.createElement('a');
  link.href = resolveAssetUrl(url);
  link.download = 'qrwifi-devices.zip';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportDeviceList = async() => {
  const res = await useApi().qrWifiApi.exportDevices(query.keyword);
  if (res.success && res.data) {
    downloadFile(res.data);
    ElMessage.success('已生成设备导出 zip 包');
  }
};

const removeDevice = async(row: QrWifiDevice) => {
  if (!row.id) {
    return;
  }
  await ElMessageBox.confirm(`确定删除设备「${row.id}」吗？删除后该设备绑定关系也会被移除。`, '删除设备确认', {
    type: 'warning',
    confirmButtonText: '确认删除',
    cancelButtonText: '取消',
  });
  const res = await useApi().qrWifiApi.deleteDevice(row.id);
  if (res.success) {
    ElMessage.success('删除成功');
    await loadData();
  }
};

onMounted(loadData);
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
